import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Music, Loader2, ArrowRight, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API, fileUrl } from '../api';
import AudioPlayer from '../components/AudioPlayer';
import RichText from '../components/RichText';
import Strings from '../components/Strings';

export default function SongShare() {
  const { code } = useParams();
  const [song, setSong] = useState(null);
  const [state, setState] = useState('loading');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    API.share(code).then((s) => { setSong(s); setState('ok'); }).catch(() => setState('missing'));
  }, [code]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setCopied(true); toast.success('Link copied.'); setTimeout(() => setCopied(false), 1800); }
    catch (e) { toast.error('Could not copy the link.'); }
  };

  return (
    <div className="min-h-screen bg-ink grain relative overflow-hidden">
      <Strings />
      <header className="relative z-10 max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="share-home-link">
          <span className="grid place-items-center w-10 h-10 rounded-full border border-gold/40 text-gold"><Music size={16} /></span>
          <span className="font-display text-xl text-cream leading-none">Brand <span className="italic text-gold-grad">Melody</span><span className="block font-mono text-[8px] tracking-[0.3em] text-cream/40 mt-1">BY P. FRAZY</span></span>
        </Link>
        <Link to="/start" className="btn-ghost rounded-full px-5 py-2.5 text-sm hidden sm:flex items-center gap-2">Get your own song <ArrowRight size={14} /></Link>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-10 pb-24">
        {state === 'loading' && <div className="grid place-items-center py-32"><Loader2 className="text-gold animate-spin" size={28} /></div>}
        {state === 'missing' && (
          <div className="panel rounded-3xl p-10 text-center" data-testid="share-missing">
            <h1 className="font-display text-4xl text-cream">This song isn&rsquo;t shared yet.</h1>
            <p className="mt-3 text-cream/60">The owner hasn&rsquo;t turned on sharing for this track. Want a song of your own?</p>
            <Link to="/start" className="btn-gold rounded-full px-7 py-3.5 mt-8 inline-flex items-center gap-2">Start your song <ArrowRight size={16} /></Link>
          </div>
        )}
        {state === 'ok' && song && (
          <div className="panel rounded-3xl p-8 md:p-12" data-testid="share-card">
            <div className="flex flex-col md:flex-row md:items-center gap-8">
              <img src={fileUrl(song.logo) || song.avatar} alt={song.business} className="w-36 h-36 rounded-3xl object-cover ring-1 ring-gold/30 bg-panel2 shrink-0" data-testid="share-logo" />
              <div className="min-w-0">
                <p className="eyebrow mb-3">{song.type || 'A Brand Melody original'}</p>
                <h1 className="font-display text-4xl sm:text-5xl text-cream leading-[0.95]" data-testid="share-business">{song.business}</h1>
                <p className="mt-3 text-cream/60"><span className="text-cream">{song.title}</span>{song.style && <> &middot; {song.style}</>}</p>
              </div>
            </div>
            <div className="mt-10 rounded-2xl border border-gold/25 bg-gold/[0.04] p-5">
              <AudioPlayer id={`share-${song.code}`} src={song.audio} />
            </div>
            {song.streaming_links && Object.values(song.streaming_links).some(Boolean) && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="share-streaming-links">
                {Object.entries(song.streaming_links).filter(([, u]) => u).map(([k, u]) => <a key={k} href={u} target="_blank" rel="noreferrer" data-testid={`share-stream-${k}`} className="btn-ghost rounded-full px-4 py-2 text-xs">Listen on {{ spotify: 'Spotify', apple_music: 'Apple Music', youtube_music: 'YouTube Music', amazon_music: 'Amazon Music' }[k] || k}</a>)}
              </div>
            )}
            {song.message && <div className="mt-8 border-l-2 border-gold/50 pl-5"><RichText text={song.message} className="text-cream/70 leading-relaxed" /><span className="block text-xs text-cream/40 mt-2">&mdash; Perry, songwriter</span></div>}
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button onClick={copy} data-testid="share-copy-btn" className="btn-ghost rounded-full px-5 py-3 text-sm flex items-center gap-2">{copied ? <Check size={14} className="text-gold" /> : <Copy size={14} />} Copy link</button>
              <Link to="/start" className="btn-gold rounded-full px-6 py-3 text-sm flex items-center gap-2" data-testid="share-cta">Get a song for your business <ArrowRight size={14} /></Link>
            </div>
          </div>
        )}
      </main>

      <footer className="relative z-10 border-t hairline py-8 text-center text-xs text-cream/40">
        Made by <Link to="/" className="text-gold hover:underline">Brand Melody</Link> &middot; Original songs for businesses, written and produced by P. Frazy.
      </footer>
    </div>
  );
}
